/**
 * StaffingCRMView — Staffing CRM (ported from the standalone GlobalCRM app)
 * as one view with internal sub-tabs:
 * Dashboard | Accounts | Contacts | Leads | Done.
 * Access: admin, operations, crm-staff (crm-staff = add/edit, no delete).
 *
 * All data lives in the staffing_* tables via /api/staffing (see
 * server/routes/staffing.js). Starts empty — every tab has an empty state
 * that opens the matching "+ New" modal.
 *
 * Deep link: #/crm-accounts?record=<id> (crm-contacts, crm-leads too) opens
 * that record's form - the Activity log's lines link here.
 *
 * NOTE: every subcomponent here is module-level (never defined inside a
 * render body) so modal inputs keep focus while typing.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { canEdit, canDelete, hasArea } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { Toast } from '../components/ui/Toast';
import { Icon } from '../components/ui/Icon';
import { EntityFormModal } from '../components/staffing/EntityFormModal';
import { ActivityLogModal } from '../components/staffing/ActivityLogModal';
import { latestReads, useLiveRefresh } from '../live/useLive';
import { parseHashFilters } from '../utils/hashParams';

const API = '/api/staffing';

// -- Enums (mirror GlobalCRM's Prisma comments) -----------------------------
const LEAD_SOURCES = ['Referral', 'Trade Show', 'FANUC ASI', 'Website', 'Cold'];
const LEAD_STATUSES = ['New', 'Working', 'Qualified', 'Done', 'Lost'];
const ACCOUNT_TYPES = ['Customer', 'Prospect', 'Partner', 'Vendor'];
const REGIONS = ['NA', 'SA', 'MEA'];

const SINGULAR = { accounts: 'Account', contacts: 'Contact', leads: 'Lead' };
// Pipeline (opportunities) + Cases were retired — the CRM is now accounts,
// contacts and leads (+ activities for follow-ups).
const LIST_ENTITIES = ['accounts', 'contacts', 'leads', 'activities'];

// -- Modal form fields per entity -------------------------------------------
const FIELDS = {
  accounts: [
    { key: 'name', label: 'Account Name', required: true, full: true },
    { key: 'type', label: 'Type', type: 'select', options: ACCOUNT_TYPES, required: true },
    { key: 'industry', label: 'Industry' },
    { key: 'region', label: 'Region', type: 'select', options: REGIONS },
    { key: 'website', label: 'Website', placeholder: 'https://…' },
    { key: 'phone', label: 'Phone' },
    { key: 'owner_name', label: 'Owner' },
  ],
  contacts: [
    { key: 'account_id', label: 'Account', type: 'account', required: true, full: true },
    { key: 'first_name', label: 'First Name', required: true },
    { key: 'last_name', label: 'Last Name', required: true },
    { key: 'title', label: 'Title' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
  ],
  leads: [
    { key: 'name', label: 'Person Name', required: true },
    { key: 'company', label: 'Company' },
    { key: 'title', label: 'Title' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'source', label: 'Source', type: 'select', options: LEAD_SOURCES },
    { key: 'status', label: 'Status', type: 'select', options: LEAD_STATUSES, required: true },
    { key: 'notes', label: 'Notes', type: 'textarea', full: true },
  ],
};

const FORM_DEFAULTS = {
  accounts: { type: 'Customer' },
  contacts: {},
  leads: { status: 'New' },
};

// -- Formatting helpers ------------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y ? `${parseInt(m, 10)}/${parseInt(d, 10)}/${y.slice(2)}` : '-';
};

// Lead statuses only — the pipeline/cases pills went away with those tabs.
const PILL_COLORS = {
  New: ['#E3F2FD', '#1565C0'],
  Working: ['#FFF8E1', '#B98900'],
  Qualified: ['#E8F5E9', '#2E7D32'],
  Done: ['#E8F5E9', '#1B5E20'],
  Lost: ['#FFEBEE', '#B71C1C'],
};
const Pill = ({ value }) => {
  const [bg, fg] = PILL_COLORS[value] || ['#EEEEEE', '#555555'];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      {value || '-'}
    </span>
  );
};

// -- Table columns per entity -----------------------------------------------
const COLUMNS = {
  accounts: [
    { key: 'name', label: 'Account', render: (r) => <span style={{ fontWeight: 700 }}>{r.name}</span> },
    { key: 'type', label: 'Type' },
    { key: 'industry', label: 'Industry' },
    { key: 'region', label: 'Region' },
    { key: 'phone', label: 'Phone' },
    { key: 'owner_name', label: 'Owner' },
    { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
  ],
  contacts: [
    {
      key: 'first_name',
      label: 'Contact',
      render: (r) => (
        <>
          <span style={{ fontWeight: 700 }}>
            {r.first_name} {r.last_name}
          </span>
          {r.title && <div style={{ fontSize: 10.5, color: '#888' }}>{r.title}</div>}
        </>
      ),
    },
    { key: 'account_name', label: 'Account' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
  ],
  leads: [
    {
      key: 'name',
      label: 'Lead',
      render: (r) => (
        <>
          <span style={{ fontWeight: 700 }}>{r.name}</span>
          {r.title && <div style={{ fontSize: 10.5, color: '#888' }}>{r.title}</div>}
        </>
      ),
    },
    { key: 'company', label: 'Company' },
    { key: 'source', label: 'Source' },
    { key: 'status', label: 'Status', render: (r) => <Pill value={r.status} /> },
    { key: 'email', label: 'Email' },
    { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
  ],
};

// -- Shared styles (match AdminUsersView) -------------------------------------
const TH = {
  textAlign: 'left',
  padding: '10px 14px',
  fontSize: 10,
  fontWeight: 800,
  color: '#888',
  letterSpacing: 1,
  textTransform: 'uppercase',
  borderBottom: '2px solid #1B5E20',
  whiteSpace: 'nowrap',
};
const TD = { padding: '12px 14px', fontSize: 12.5, color: '#0D0D0D', borderBottom: '1px solid #EEE' };
const NEW_BTN = {
  padding: '8px 16px',
  borderRadius: 6,
  border: 'none',
  background: '#1B5E20',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
const ROW_BTN = {
  background: 'none',
  border: '1px solid #E0E0E0',
  borderRadius: 6,
  padding: '5px 7px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: 6,
};

// -- Module-level subcomponents -----------------------------------------------
function SubTabs({ tabs, active, onSelect }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '2px solid #E4E7E2', marginBottom: 18 }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onSelect(t.id)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            padding: '9px 14px',
            fontSize: 12,
            fontWeight: active === t.id ? 800 : 600,
            color: active === t.id ? '#1B5E20' : '#666',
            borderBottom: active === t.id ? '3px solid #1B5E20' : '3px solid transparent',
            marginBottom: -2,
          }}
        >
          {t.label}
          {t.count != null ? ` · ${t.count}` : ''}
        </button>
      ))}
    </div>
  );
}

function EmptyBox({ icon, title, sub, actionLabel, onAction }) {
  return (
    <div
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        background: '#fff',
        border: '1px dashed #D8DED4',
        borderRadius: 12,
      }}
    >
      <div style={{ display: 'inline-flex', padding: 14, borderRadius: '50%', background: '#F1F8E9', marginBottom: 12 }}>
        <Icon name={icon} size={22} color="#1B5E20" />
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#0D0D0D', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#777', marginBottom: 16 }}>{sub}</div>
      {actionLabel && (
        <button type="button" style={NEW_BTN} onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function EntityTable({ columns, rows, actions }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #ECECEC',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={TH}>
                  {c.label}
                </th>
              ))}
              <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                {columns.map((c) => (
                  <td key={c.key} style={TD}>
                    {c.render ? c.render(r) : (r[c.key] ?? '-')}
                  </td>
                ))}
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>{actions(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div
      style={{
        flex: '1 1 160px',
        minWidth: 160,
        background: '#fff',
        border: '1px solid #ECECEC',
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 800, color: '#888', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div className="tabular-nums" style={{ fontSize: 24, fontWeight: 800, color: '#1B5E20' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: '#999', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// -- Main view ------------------------------------------------------------------
export default function StaffingCRMView({ section }) {
  const { users } = useApp();
  const { user } = useAuth();
  const access = useMyAccess();
  const canUseCrm = hasArea(access, 'staffing', 'view');
  const editable = canEdit(access, 'staffing'); // Staffing CRM Edit (admin/operations/crm-staff by role)
  const deletable = canDelete(access, 'staffing'); // crm-staff's own role level does not delete

  // When rendered as a dedicated sidebar tab (section prop), that section is
  // fixed and the internal sub-tab bar is hidden; standalone mode keeps tabs.
  const [tab, setTab] = useState(section || 'dashboard');
  useEffect(() => {
    if (section) setTab(section);
  }, [section]);
  const [data, setData] = useState({ accounts: [], contacts: [], leads: [], activities: [] });
  // My display name — used as the default owner on new records.
  const myName = user?.name || user?.email || '';
  // Which record's activity timeline is open: { record, recordType, recordLabel }
  const [activityFor, setActivityFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState('');
  const clearToast = useCallback(() => setToast(''), []);

  // modal = { entity, record|null }; form state lives here so the module-level
  // EntityFormModal never remounts its inputs mid-keystroke.
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // `quiet` (a live re-read): a failure keeps the data and any error as they
  // are - someone else's save must never turn this screen into "Couldn't
  // load"; only a load the person is waiting on says it failed.
  // The reads overlap (a live re-read still out when a save re-reads): the
  // newest answer shown wins, so an older one landing last never puts back
  // lists without the record just saved here (latestReads).
  const [reads] = useState(latestReads);
  const fetchAll = useCallback(
    async (quiet) => {
      const t = reads.start();
      try {
        const results = await Promise.all(
          LIST_ENTITIES.map((e) =>
            fetch(`${API}/${e}`).then((r) => {
              if (!r.ok) throw new Error(`Failed to load ${e} (HTTP ${r.status})`);
              return r.json();
            })
          )
        );
        if (!reads.show(t)) return;
        setData(Object.fromEntries(LIST_ENTITIES.map((e, i) => [e, results[i]])));
        setLoadError('');
      } catch (err) {
        if (quiet !== true && reads.show(t)) setLoadError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [reads]
  );

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  // a record someone else adds or edits shows up without a refresh (src/live)
  const refetchQuietly = useCallback(() => fetchAll(true), [fetchAll]);
  useLiveRefresh('staffing', refetchQuietly);

  const mutate = async (method, path, body) => {
    const res = await fetch(`${API}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || `Request failed (HTTP ${res.status})`);
    }
    return res.json();
  };

  const openNew = (entity) => {
    // New records default to the signed-in BD as owner (Salesforce behavior).
    const defaults = { ...FORM_DEFAULTS[entity] };
    if (FIELDS[entity].some((f) => f.key === 'owner_name') && myName) defaults.owner_name = myName;
    setForm(defaults);
    setFormError('');
    setModal({ entity, record: null });
  };

  const openEdit = useCallback((entity, record) => {
    const init = {};
    FIELDS[entity].forEach((f) => {
      const v = record[f.key];
      init[f.key] = v == null ? '' : f.type === 'date' ? String(v).slice(0, 10) : v;
    });
    setForm(init);
    setFormError('');
    setModal({ entity, record });
  }, []);

  // Deep link #/crm-accounts?record=<id> (or crm-contacts / crm-leads; the
  // Activity log's lines open these): that record's form, once the lists have
  // loaded, for someone who may edit it. Once per link.
  const [linked, setLinked] = useState(() => parseHashFilters().record || null);
  useEffect(() => {
    const onHash = () => setLinked(parseHashFilters().record || null);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    if (!linked || loading) return;
    const rec = ['accounts', 'contacts', 'leads'].includes(tab) ? (data[tab] || []).find((r) => String(r.id) === linked) : null;
    if (rec && editable) openEdit(tab, rec);
    setLinked(null);
  }, [linked, loading, data, tab, editable, openEdit]);

  const onFieldChange = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const submitForm = async () => {
    if (!modal) return;
    const fields = FIELDS[modal.entity];
    const missing = fields.filter((f) => f.required && String(form[f.key] ?? '').trim() === '');
    if (missing.length) {
      setFormError(`Please fill in: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {};
      fields.forEach((f) => {
        if (form[f.key] !== undefined) payload[f.key] = form[f.key];
      });
      if (modal.record) {
        await mutate('PUT', `/${modal.entity}/${modal.record.id}`, payload);
        setToast(`${SINGULAR[modal.entity]} updated`);
      } else {
        await mutate('POST', `/${modal.entity}`, payload);
        setToast(`${SINGULAR[modal.entity]} created`);
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (entity, rec) => {
    const label = entity === 'contacts' ? `${rec.first_name} ${rec.last_name}` : rec.name || rec.subject || rec.id;
    if (!window.confirm(`Delete ${SINGULAR[entity].toLowerCase()} "${label}"? This cannot be undone.`)) return;
    try {
      await mutate('DELETE', `/${entity}/${rec.id}`);
      setToast(`${SINGULAR[entity]} deleted`);
      fetchAll();
    } catch (err) {
      window.alert(err.message);
    }
  };

  const convertLead = async (lead) => {
    try {
      await mutate('POST', `/leads/${lead.id}/convert`);
      setToast('Lead marked Done — account & contact created');
      fetchAll();
    } catch (err) {
      window.alert(err.message);
    }
  };

  // Transfer an account to a different owner (dropdown of registry users).
  const transferOwner = async (account, ownerName) => {
    try {
      await mutate('PUT', `/accounts/${account.id}`, { owner_name: ownerName || null });
      setToast(ownerName ? `Owner set to ${ownerName}` : 'Owner cleared');
      fetchAll();
    } catch (err) {
      window.alert(err.message);
    }
  };

  if (!canUseCrm) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: "'Montserrat', sans-serif" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#B71C1C', marginBottom: 6 }}>No access</div>
        <div style={{ fontSize: 12, color: '#666' }}>You don't have access to the CRM.</div>
      </div>
    );
  }

  const { accounts = [], contacts = [], leads = [], activities = [] } = data;
  const openLeads = leads.filter((l) => l.status !== 'Done' && l.status !== 'Lost');
  const doneLeads = leads.filter((l) => l.status === 'Done');

  // -- Task worklist (dashboard): open activities bucketed by due date --------
  const todayStr = new Date().toISOString().slice(0, 10);
  const weekOut = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const openActs = (activities || []).filter((a) => !a.done);
  const dueOf = (a) => (a.due_date ? String(a.due_date).slice(0, 10) : null);
  const tasksOverdue = openActs.filter((a) => dueOf(a) && dueOf(a) < todayStr);
  const tasksToday = openActs.filter((a) => dueOf(a) === todayStr);
  const tasksUpcoming = openActs.filter((a) => dueOf(a) && dueOf(a) > todayStr && dueOf(a) <= weekOut);
  const actRecordLabel = (a) => {
    if (a.account_id) return accounts.find((x) => x.id === a.account_id)?.name;
    return null;
  };
  const completeActivity = async (a) => {
    try {
      await mutate('PUT', `/activities/${a.id}`, { done: true });
      fetchAll();
    } catch (err) {
      window.alert(err.message);
    }
  };
  // Count of activities per record for the row badges.
  const actCount = (fk, id) => (activities || []).filter((a) => a[fk] === id && !a.done).length;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'accounts', label: 'Accounts', count: accounts.length },
    { id: 'contacts', label: 'Contacts', count: contacts.length },
    { id: 'leads', label: 'Leads', count: openLeads.length },
    { id: 'done', label: 'Done', count: doneLeads.length },
  ];

  // standard row actions — gated by role (editable = create/edit; deletable
  // excludes crm-staff). editable is always true for CRM viewers today, but the
  // gate keeps a future view-only CRM role safe.
  const rowActions = (entity) => (r) => (
    <>
      {entity === 'accounts' && editable && (
        <select
          value={r.owner_name || ''}
          onChange={(e) => transferOwner(r, e.target.value)}
          title="Transfer owner"
          style={{ ...ROW_BTN, padding: '4px 6px', fontFamily: 'inherit', fontSize: 10.5, color: '#333', maxWidth: 150 }}
        >
          <option value="">- owner -</option>
          {r.owner_name && !users.some((u) => (u.name || u.email) === r.owner_name) && <option value={r.owner_name}>{r.owner_name}</option>}
          {users.map((u) => {
            const nm = u.name || u.email;
            return (
              <option key={u.email} value={nm}>
                {nm}
              </option>
            );
          })}
        </select>
      )}
      {entity === 'accounts' && editable && (
        <button
          type="button"
          title="Activities & follow-ups"
          onClick={() => setActivityFor({ record: r, recordType: 'accounts', recordLabel: r.name })}
          style={{
            ...ROW_BTN,
            padding: '5px 10px',
            fontSize: 10.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            color: '#1B5E20',
            border: '1px solid #C8E6C9',
          }}
        >
          Log{actCount('account_id', r.id) > 0 ? ` · ${actCount('account_id', r.id)}` : ''}
        </button>
      )}
      {entity === 'leads' && editable && r.status !== 'Done' && r.status !== 'Lost' && (
        <button
          type="button"
          title="Mark done — creates an account + contact and archives the lead"
          onClick={() => convertLead(r)}
          style={{
            ...ROW_BTN,
            padding: '5px 10px',
            border: '1px solid #C8E6C9',
            color: '#1B5E20',
            fontSize: 10.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            letterSpacing: 0.5,
          }}
        >
          Done ▸
        </button>
      )}
      {editable && (
        <button type="button" title="Edit" style={ROW_BTN} onClick={() => openEdit(entity, r)}>
          <Icon name="edit" size={13} color="#555" />
        </button>
      )}
      {deletable && (
        <button type="button" title="Delete" style={ROW_BTN} onClick={() => deleteRecord(entity, r)}>
          <Icon name="trash" size={13} color="#B71C1C" />
        </button>
      )}
    </>
  );

  const entityHeader = (entity, countText) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <span style={{ fontSize: 12, color: '#666' }}>{countText}</span>
      {editable && (
        <button type="button" style={NEW_BTN} onClick={() => openNew(entity)}>
          <Icon name="plus" size={13} color="#fff" /> New {SINGULAR[entity]}
        </button>
      )}
    </div>
  );

  const EMPTY = {
    accounts: {
      icon: 'crm',
      title: 'No accounts yet',
      sub: 'Accounts are the companies you staff for. Add your first one to get started.',
    },
    contacts: {
      icon: 'user',
      title: 'No contacts yet',
      sub: 'Contacts are the people at your accounts. Add an account first, then its contacts.',
    },
    leads: {
      icon: 'trending',
      title: 'No leads yet',
      sub: 'Capture inbound interest here. Marking a lead Done creates an account + contact and archives it.',
    },
    done: { icon: 'trending', title: 'No completed leads yet', sub: 'Leads you mark Done show up here as a history of finished leads.' },
  };

  // `rows` lets callers pass a filtered slice (e.g. open vs. Done leads).
  // readOnly renders a history table with no header/actions (the Done tab).
  const renderEntityTab = (entity, rows = data[entity], opts = {}) => {
    if (rows.length === 0) {
      const e = EMPTY[opts.emptyKey || entity];
      return (
        <>
          {!opts.readOnly && entityHeader(entity, `0 ${entity}`)}
          <EmptyBox
            {...e}
            actionLabel={editable && !opts.readOnly ? `+ New ${SINGULAR[entity]}` : undefined}
            onAction={() => openNew(entity)}
          />
        </>
      );
    }
    return (
      <>
        {opts.readOnly ? (
          <div style={{ marginBottom: 12, fontSize: 12, color: '#666' }}>
            {rows.length} completed {rows.length === 1 ? 'lead' : 'leads'}
          </div>
        ) : (
          entityHeader(entity, `${rows.length} ${rows.length === 1 ? SINGULAR[entity].toLowerCase() : entity}`)
        )}
        <EntityTable
          columns={COLUMNS[entity]}
          rows={rows}
          actions={opts.readOnly ? () => <span style={{ color: '#bbb', fontSize: 11 }}>-</span> : rowActions(entity)}
        />
      </>
    );
  };

  const totalRecords = LIST_ENTITIES.reduce((s, e) => s + data[e].length, 0);

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#1B5E20', letterSpacing: 2, textTransform: 'uppercase' }}>
          Admin · Staffing CRM
        </div>
        <p style={{ fontSize: 12.5, color: '#555', margin: '4px 0 0' }}>
          Business development for staffing — accounts, contacts and leads.
        </p>
      </div>

      {!section && <SubTabs tabs={tabs} active={tab} onSelect={setTab} />}

      {loading && <div style={{ padding: 48, textAlign: 'center', fontSize: 12, color: '#888' }}>Loading Staffing CRM…</div>}

      {!loading && loadError && (
        <div
          style={{
            padding: '16px 20px',
            background: '#FFEBEE',
            border: '1px solid #FFCDD2',
            borderRadius: 10,
            fontSize: 12,
            color: '#B71C1C',
          }}
        >
          <b>Couldn't load Staffing CRM data.</b> {loadError}
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              fetchAll();
            }}
            style={{ ...NEW_BTN, marginLeft: 12, padding: '6px 12px', background: '#B71C1C' }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading &&
        !loadError &&
        tab === 'dashboard' &&
        (totalRecords === 0 ? (
          <EmptyBox
            icon="dashboard"
            title="Your staffing CRM is empty"
            sub="Start by adding an account you staff for, or capture your first lead."
            actionLabel={editable ? '+ New Account' : undefined}
            onAction={() => {
              setTab('accounts');
              openNew('accounts');
            }}
          />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <StatCard label="Accounts" value={accounts.length} />
              <StatCard label="Contacts" value={contacts.length} />
              <StatCard label="Open Leads" value={openLeads.length} sub={`${leads.length} total`} />
              <StatCard label="Completed" value={doneLeads.length} sub="leads marked done" />
            </div>

            {/* TODAY'S WORK — overdue / today / next 7 days follow-ups */}
            <div
              style={{
                marginTop: 20,
                background: '#fff',
                border: '1px solid #ECECEC',
                borderRadius: 12,
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              }}
            >
              <div
                style={{
                  padding: '14px 18px',
                  borderBottom: '1px solid #F0F0F0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 800, color: '#0D0D0D' }}>Today&rsquo;s Work</span>
                <span style={{ fontSize: 11, color: '#888' }}>
                  {openActs.length} open {openActs.length === 1 ? 'task' : 'tasks'}
                </span>
              </div>
              <div style={{ padding: '12px 18px' }}>
                {[
                  ['Overdue', tasksOverdue, '#B71C1C'],
                  ['Due Today', tasksToday, '#B98900'],
                  ['Next 7 Days', tasksUpcoming, '#1B5E20'],
                ].map(
                  ([label, list, color]) =>
                    list.length > 0 && (
                      <div key={label} style={{ marginBottom: 10 }}>
                        <div
                          style={{ fontSize: 10, fontWeight: 800, color, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}
                        >
                          {label} ({list.length})
                        </div>
                        {list.map((a) => (
                          <div
                            key={a.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '7px 10px',
                              borderRadius: 8,
                              border: '1px solid #F0F0F0',
                              marginBottom: 4,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={false}
                              onChange={() => completeActivity(a)}
                              style={{ accentColor: '#1B5E20', cursor: 'pointer' }}
                              aria-label="Mark done"
                            />
                            <span
                              style={{
                                fontSize: 9.5,
                                fontWeight: 800,
                                letterSpacing: 0.6,
                                textTransform: 'uppercase',
                                color: '#1B5E20',
                                background: '#F1F8E9',
                                padding: '2px 7px',
                                borderRadius: 999,
                              }}
                            >
                              {a.type}
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#0D0D0D',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {a.subject}
                            </span>
                            {actRecordLabel(a) && <span style={{ fontSize: 11, color: '#666' }}>{actRecordLabel(a)}</span>}
                            <span className="tabular-nums" style={{ fontSize: 11, color, fontWeight: 700 }}>
                              {fmtDate(a.due_date)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                )}
                {tasksOverdue.length + tasksToday.length + tasksUpcoming.length === 0 && (
                  <div style={{ padding: '14px 0', fontSize: 12, color: '#888', textAlign: 'center' }}>
                    Nothing due this week. Log a follow-up from any account (&ldquo;Log&rdquo;) or deal card.
                  </div>
                )}
              </div>
            </div>
          </>
        ))}

      {!loading && !loadError && (tab === 'accounts' || tab === 'contacts') && renderEntityTab(tab)}
      {!loading && !loadError && tab === 'leads' && renderEntityTab('leads', openLeads)}
      {!loading && !loadError && tab === 'done' && renderEntityTab('leads', doneLeads, { readOnly: true, emptyKey: 'done' })}

      {activityFor && (
        <ActivityLogModal
          record={activityFor.record}
          recordType={activityFor.recordType}
          recordLabel={activityFor.recordLabel}
          myName={myName}
          onClose={() => setActivityFor(null)}
          onChanged={fetchAll}
        />
      )}

      {modal && (
        <EntityFormModal
          title={`${modal.record ? 'Edit' : 'New'} ${SINGULAR[modal.entity]}`}
          fields={FIELDS[modal.entity]}
          form={form}
          accounts={accounts}
          error={formError}
          saving={saving}
          onChange={onFieldChange}
          onSubmit={submitForm}
          onClose={() => setModal(null)}
        />
      )}

      {toast && <Toast message={toast} onDone={clearToast} />}
    </div>
  );
}
